import { useRef } from "react";
import { motion, useScroll } from "framer-motion";
import { Reveal, SectionHeading, SectionCta } from "./Reveal";

const STEPS = [
  { time: "2:14 AM", title: "Form submitted", sub: "Facebook ad. $84 cost per lead.", delta: "+0m" },
  { time: "2:14 AM", title: "Auto-reply sent", sub: '"We will be in touch during business hours."', delta: "+0m" },
  { time: "9:12 AM", title: "First call attempt", sub: "No answer. Voicemail left.", delta: "+6h 58m" },
  { time: "2:40 PM", title: "Second attempt", sub: "No answer. No third attempt was ever made.", delta: "+12h 26m" },
  { time: "Thu, 2:19 AM", title: "Booked with another practice", sub: "They answered the same night the inquiry came in.", delta: "Lost", lost: true },
];

const STATS = [
  { label: "Time to first attempt", value: "7 hrs" },
  { label: "Follow-up attempts made", value: "2" },
  { label: "Cases lost this way", value: "1 case" },
];

export const LostLead = () => {
  const timelineRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: timelineRef,
    offset: ["start 75%", "end 55%"],
  });

  return (
    <section data-testid="lost-lead-section" className="py-24 md:py-36 bg-coal border-y border-black/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <SectionHeading
            eyebrow="This is not hypothetical"
            title={
              <>
                The exact shape of <span className="italic text-champagne">a lost consult.</span>
              </>
            }
            copy="Pulled from a real intake log, before the Engine was installed. It is almost never a traffic problem. It is a speed and follow-through problem."
          />
          <Reveal delay={0.2} className="mt-10 grid grid-cols-3 gap-px bg-black/10 border border-black/10">
            {STATS.map((s) => (
              <div key={s.label} className="bg-obsidian px-4 py-5" data-testid={`lost-stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
                <p className="font-mono text-xl text-pearl">{s.value}</p>
                <p className="mt-2 text-[11px] leading-snug text-zinc-500">{s.label}</p>
              </div>
            ))}
          </Reveal>
          <Reveal delay={0.3}>
            <p className="mt-8 text-sm text-zinc-500 leading-relaxed max-w-sm">
              The Back-Office Engine answers in under five minutes, and it does not stop at two attempts.
            </p>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <div className="relative pl-10" ref={timelineRef}>
            <div className="absolute left-[7px] top-2 bottom-2 w-px bg-black/10" />
            <motion.div
              style={{ scaleY: scrollYProgress }}
              className="absolute left-[7px] top-2 bottom-2 w-px origin-top bg-gradient-to-b from-champagne via-champagne/60 to-red-500"
            />
            {STEPS.map((s, i) => (
              <Reveal key={i} delay={i * 0.1} y={24}>
                <div className="relative pb-10 last:pb-0" data-testid={`timeline-step-${i}`}>
                  <motion.span
                    animate={s.lost ? { scale: [1, 1.25, 1] } : {}}
                    transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
                    className={`absolute -left-10 top-1.5 h-[15px] w-[15px] rounded-full border-2 ${
                      s.lost
                        ? "border-red-500 bg-red-500/20 shadow-[0_0_16px_rgba(239,68,68,0.4)]"
                        : "border-champagne bg-white shadow-[0_0_12px_rgba(61,165,221,0.4)]"
                    }`}
                  />
                  <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                    <span className={`font-mono text-sm ${s.lost ? "text-red-500" : "text-champagne"}`}>{s.time}</span>
                    <span className="font-mono text-[11px] tracking-widest uppercase text-zinc-500">{s.delta}</span>
                  </div>
                  <h4 className={`mt-2 font-display text-2xl ${s.lost ? "text-red-500" : "text-pearl"}`}>{s.title}</h4>
                  <p className="mt-1 text-sm text-zinc-500">{s.sub}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <SectionCta id="lost-lead" label="Stop losing these patients" />
      </div>
    </section>
  );
};
